import './AllFeaturesTable.scss'

import React, { Fragment, FunctionComponent, useEffect, useState } from 'react'
import { getModalTriggerId } from 'Root/Shared/components/PuxModal/PuxModal'
import PuxButton from 'Shared/components/PuxButton/PuxButton'
import PuxIcon from 'Shared/components/PuxIcon/PuxIcon'
import PuxTooltip from 'Shared/components/PuxTooltip/PuxTooltip'
import { t } from 'ttag'

import {
  formatCurrencyPrice,
  getDefaultCurrencyIndex,
  getFeatureInputHtml,
  getPricingIsoCode,
  isFeatureAvailable,
} from './pricingFormatters'
import { ALL_PRICING_PLANS, PricingPlan, PricingPlanId } from './pricingPlans'
import {
  MaybeList,
  PricingCurrencyType,
  PricingFeatureCategoryType,
  PricingFeatureType,
} from './pricingTypes'

interface AllFeaturesTableType {
  allFeatures: MaybeList<PricingFeatureCategoryType>
  currencies?: MaybeList<PricingCurrencyType>
}

const project: string = process.env.PROJECT
  ? process.env.PROJECT
  : `EasyRedmineGatsby`
const isoCode = getPricingIsoCode(project, process.env.LOCALE)

const AllFeatures: FunctionComponent<AllFeaturesTableType> = (props) => {
  const categories = Array.isArray(props.allFeatures) ? props.allFeatures : []
  const currencies = Array.isArray(props.currencies) ? props.currencies : []
  const [currencyIdx, setCurrencyIdx] = useState(0)
  const [openedMobileSection, setOpenedMobileSection] = useState(`Essential-0`)

  const getDefaultCurrency = () => {
    const cookie = document.cookie.match(
      `(^|;)\\s*` + `awscountry` + `\\s*=\\s*([^;]+)`
    )

    setCurrencyIdx(getDefaultCurrencyIndex(currencies, cookie?.[2]))
  }

  useEffect(() => {
    getDefaultCurrency()
  }, [currencies])

  const openModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    const trigger = document.getElementById(getModalTriggerId(`talk`))
    if (trigger) {
      trigger.click()
    }
  }

  const getPrice = (plan: PricingPlan) => {
    const currency = currencies[currencyIdx]
    if (!currency || plan.enterprise) return undefined

    const price = formatCurrencyPrice(currency, plan.id, isoCode)

    return (
      <p className='AllFeatures-plan-price-value'>
        {price.currencyLast ? `` : price.currency}
        {price.integer.length ? price.integer.join(``) : ``}
        <sup>{price.fraction.length ? price.fraction.join(``) : ``}</sup>
        {price.currencyLast ? price.currency : ``}
      </p>
    )
  }

  const getFeatureInfo = (feature: PricingFeatureType) => {
    const title = feature.pricingFeatureTooltip || feature.displayText

    return (
      <PuxTooltip content={title} placement='top'>
        <span
          aria-label={title}
          className='AllFeatures-info'
          role='img'
          tabIndex={0}
        >
          <PuxIcon name='user-interface/c-info' size={16} decorative />
        </span>
      </PuxTooltip>
    )
  }

  const getPlanAction = (plan: PricingPlan) => {
    if (plan.enterprise) {
      return (
        <PuxButton
          buttonType='border'
          buttonSize='medium'
          buttonLink={{
            url: [`/`],
            internal: [true],
            text: [t`Redmine.Pricing.LetsTalk`],
          }}
          buttonOpenModalId='talk'
        />
      )
    }

    return (
      <PuxButton
        buttonType={plan.highlighted ? `primary` : `border`}
        buttonSize='medium'
        buttonLink={{
          url: [`#trial`],
          internal: [false],
          text: [t`Redmine.Pricing.TryIt`],
        }}
      />
    )
  }

  const getPlanHeading = (plan: PricingPlan) => (
    <>
      {plan.highlighted ? (
        <div className='AllFeatures-sticker'>
          <span>{t`Redmine.Pricing.MostPopular`}</span>
        </div>
      ) : null}
      <h3>{plan.title}</h3>
    </>
  )

  const getPlanPrice = (plan: PricingPlan) => (
    <div className='AllFeatures-plan-price'>
      {plan.enterprise ? (
        <a
          className='AllFeatures-plan-price-text'
          data-open-modal-id='talk'
          onClick={openModal}
        >{t`Redmine.Pricing.Enterprice.ContactUs`}</a>
      ) : (
        <>
          {getPrice(plan)}
          <p className='AllFeatures-plan-amount'>
            {t`Redmine.Pricing.UserPerMonth`}
          </p>
        </>
      )}
    </div>
  )

  const getPlanHeader = (plan: PricingPlan) => (
    <div
      className={`AllFeatures-plan-header AllFeatures-plan-header--${plan.id.toLowerCase()}${
        plan.highlighted ? ` AllFeatures-plan-header--highlighted` : ``
      }`}
    >
      {getPlanHeading(plan)}
      {getPlanPrice(plan)}
      {getPlanAction(plan)}
    </div>
  )

  const getFeatureValue = (
    feature: PricingFeatureType,
    plan: PricingPlanId
  ) => {
    const available = isFeatureAvailable(feature, plan)
    const inputHtml = getFeatureInputHtml(feature, plan)

    if (available) {
      return (
        <span
          className='AllFeatures-cell-icon AllFeatures-cell-icon--check'
          aria-label='Included'
        >
          <PuxIcon name='user-interface/check' size={16} decorative />
        </span>
      )
    }

    if (inputHtml) {
      return (
        <div
          className='AllFeatures-cell-text'
          dangerouslySetInnerHTML={{ __html: inputHtml }}
        />
      )
    }

    return (
      <PuxIcon
        className='AllFeatures-cell-icon AllFeatures-cell-icon--empty'
        name='user-interface/e-delete'
        size={24}
        title='Not included'
      />
    )
  }

  return (
    <div className={`AllFeatures`}>
      <div className={`AllFeatures-desktop`}>
        <table>
          <colgroup>
            <col className='AllFeatures-feature-column' />
            {ALL_PRICING_PLANS.map((plan) => (
              <col key={plan.id} />
            ))}
          </colgroup>
          <thead>
            <tr className='AllFeatures-plan-title-row'>
              <th aria-label='Feature' rowSpan={3}></th>
              {ALL_PRICING_PLANS.map((plan) => (
                <th key={plan.id} scope='col'>
                  <div className='AllFeatures-plan-title'>
                    {getPlanHeading(plan)}
                  </div>
                </th>
              ))}
            </tr>
            <tr className='AllFeatures-plan-price-row'>
              {ALL_PRICING_PLANS.map((plan) => (
                <td key={plan.id}>{getPlanPrice(plan)}</td>
              ))}
            </tr>
            <tr className='AllFeatures-plan-action-row'>
              {ALL_PRICING_PLANS.map((plan) => (
                <td key={plan.id}>
                  <div className='AllFeatures-plan-action'>
                    {getPlanAction(plan)}
                  </div>
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((item) => (
              <Fragment key={item.contentItemId}>
                <tr className='AllFeatures-section-row'>
                  <th colSpan={ALL_PRICING_PLANS.length + 1} scope='colgroup'>
                    {item.displayText}
                  </th>
                </tr>
                {item.pricingFeatureCategoryItems?.contentItems?.map(
                  (feature, idx) => (
                    <tr
                      className={`AllFeatures-feature-row${
                        idx % 2 === 0 ? ` AllFeatures-feature-row--subtle` : ``
                      }`}
                      key={feature.contentItemId ?? idx}
                    >
                      <th scope='row'>
                        <span className='AllFeatures-feature-label'>
                          <span>{feature.displayText}</span>
                          {feature.pricingFeatureTooltip
                            ? getFeatureInfo(feature)
                            : null}
                        </span>
                      </th>
                      {ALL_PRICING_PLANS.map((plan) => (
                        <td key={plan.id}>
                          {getFeatureValue(feature, plan.id)}
                        </td>
                      ))}
                    </tr>
                  )
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className={`AllFeatures-mobile`}>
        {ALL_PRICING_PLANS.map((plan) => (
          <div
            className={`AllFeatures-card${
              plan.highlighted ? ` AllFeatures-card--highlighted` : ``
            }`}
            key={plan.id}
          >
            {getPlanHeader(plan)}
            {categories.map((item, index) => {
              const sectionId = `${plan.id}-${index}`
              const isOpen = openedMobileSection === sectionId

              return (
                <div
                  className={`AllFeatures-mobile-section${
                    isOpen ? ` AllFeatures-mobile-section--open` : ``
                  }`}
                  key={`${plan.id}-${item.contentItemId}`}
                >
                  <button
                    aria-controls={`AllFeatures-${sectionId}`}
                    aria-expanded={isOpen}
                    className='AllFeatures-mobile-toggle'
                    onClick={() =>
                      setOpenedMobileSection(isOpen ? `` : sectionId)
                    }
                    type='button'
                  >
                    <span>{item.displayText}</span>
                    <span className='AllFeatures-chevron' />
                  </button>
                  {isOpen ? (
                    <div
                      className='AllFeatures-mobile-list'
                      id={`AllFeatures-${sectionId}`}
                    >
                      {item.pricingFeatureCategoryItems?.contentItems?.map(
                        (feature, idx) => (
                          <div
                            className={`AllFeatures-mobile-item${
                              idx % 2 === 0
                                ? ` AllFeatures-mobile-item--subtle`
                                : ``
                            }`}
                            key={`${plan.id}-${feature.contentItemId ?? idx}`}
                          >
                            <span className='AllFeatures-mobile-item-label'>
                              <span>{feature.displayText}</span>
                              {feature.pricingFeatureTooltip
                                ? getFeatureInfo(feature)
                                : null}
                            </span>
                            {getFeatureValue(feature, plan.id)}
                          </div>
                        )
                      )}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

export default AllFeatures
